// Phase 27.2 — Real-Time Inventory + Variance Dashboard
// - 4 KPI cards (Stock-on-Hand, SKU Shortfall, Aging Stock, Negative Variance)
// - 30s poll (setInterval, paused when tab hidden)
// - Manual refresh + "Updated Xs ago" indicator
// - Each card respects VisibilityResolver (backend)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Boxes, TrendingDown, Clock, AlertTriangle, ChevronDown, ChevronRight, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 30_000;

// -------------------------------------------------- helpers
function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function fmtInr(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function fmtKg(n)  { return (n == null ? "—" : `${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`); }
function relTime(ts, now) {
  if (!ts) return "—";
  const s = Math.max(0, Math.round((now - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// -------------------------------------------------- card shell
function CardShell({ icon: Icon, title, subtitle, lastFetch, loading, onRefresh, actions, empty, children, testid }) {
  const now = useNow();
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-5 flex flex-col gap-3 min-h-[340px]"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-[#E5197F]/10 text-[#E5197F]"><Icon className="h-5 w-5" /></div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
            {subtitle ? <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          <span className="text-[11px] text-neutral-400" data-testid={`${testid}-last-fetch`}>
            {lastFetch ? `Updated ${relTime(lastFetch, now)}` : loading ? "Loading…" : "—"}
          </span>
          <Button size="icon" variant="ghost" onClick={onRefresh} disabled={loading}
                  data-testid={`${testid}-refresh`} className="h-8 w-8">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </header>
      <div className="flex-1 min-h-0">
        {loading && !lastFetch ? (
          <div className="animate-pulse space-y-2 pt-3">
            <div className="h-8 bg-neutral-100 rounded" />
            <div className="h-8 bg-neutral-100 rounded" />
            <div className="h-8 bg-neutral-100 rounded" />
          </div>
        ) : empty ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500 pt-6" data-testid={`${testid}-empty`}>
            {empty}
          </div>
        ) : children}
      </div>
    </section>
  );
}

// -------------------------------------------------- Card A
function StockOnHandCard({ data, loading, lastFetch, onRefresh }) {
  const [openZones, setOpenZones] = useState({});
  const isEmpty = !data || !data.zones?.length;
  return (
    <CardShell
      testid="card-stock-on-hand" icon={Boxes}
      title="Stock-on-hand by zone" subtitle={data ? `Grand total: ${fmtKg(data.grand_total?.kg)} · ${fmtInr(data.grand_total?.value_inr)}` : ""}
      lastFetch={lastFetch} loading={loading} onRefresh={onRefresh}
      empty={isEmpty ? "No stock rows in your scope" : null}>
      {!isEmpty && (
        <div className="overflow-auto max-h-[260px] pr-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Zone</TableHead>
                <TableHead className="text-right">KG</TableHead>
                <TableHead className="text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.zones.map((z) => {
                const open = !!openZones[z.zone_id];
                return (
                  <>
                    <TableRow key={z.zone_id} className="cursor-pointer hover:bg-neutral-50"
                              data-testid={`row-zone-${z.zone_id}`}
                              onClick={() => setOpenZones((s) => ({ ...s, [z.zone_id]: !open }))}>
                      <TableCell>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                      <TableCell className="font-medium">{z.zone_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtKg(z.kg)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtInr(z.value_inr)}</TableCell>
                    </TableRow>
                    {open && z.distributors.map((d) => (
                      <TableRow key={d.distributor_id} className="bg-neutral-50/50" data-testid={`row-dist-${d.distributor_id}`}>
                        <TableCell></TableCell>
                        <TableCell className="pl-6 text-sm text-neutral-600">↳ {d.code} · {d.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtKg(d.kg)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtInr(d.value_inr)}</TableCell>
                      </TableRow>
                    ))}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </CardShell>
  );
}

// -------------------------------------------------- Card B
function SkuShortfallCard({ data, loading, lastFetch, onRefresh }) {
  const [dim, setDim] = useState("kg"); // kg | inr
  const isEmpty = !data || !data.items?.length;
  const val = (i) => dim === "kg" ? fmtKg(i.shortfall_kg) : fmtInr(i.shortfall_inr);
  const actions = (
    <Select value={dim} onValueChange={setDim}>
      <SelectTrigger className="h-8 w-[100px] text-xs" data-testid="card-shortfall-dim"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="kg">KG</SelectItem>
        <SelectItem value="inr">INR</SelectItem>
      </SelectContent>
    </Select>
  );
  return (
    <CardShell
      testid="card-sku-shortfall" icon={TrendingDown}
      title="SKU shortfall vs monthly target"
      subtitle={data ? `Period ${data.period} · Top ${data.items?.length || 0}` : ""}
      lastFetch={lastFetch} loading={loading} onRefresh={onRefresh}
      actions={actions}
      empty={isEmpty ? "No SKU shortfalls in the current period" : null}>
      {!isEmpty && (
        <div className="overflow-auto max-h-[260px] pr-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Gap</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((i, idx) => (
                <TableRow key={`${i.sku_id}-${i.owner_id}-${idx}`} data-testid={`row-shortfall-${idx}`}>
                  <TableCell className="font-medium">{i.sku_code}<div className="text-xs text-neutral-500">{i.sku_name}</div></TableCell>
                  <TableCell className="text-xs text-neutral-600">{i.owner_type}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKg(i.target_kg)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKg(i.actual_kg)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#E5197F] font-medium">{val(i)}</TableCell>
                  <TableCell className="text-right"><Badge variant="destructive" className="text-[11px]">{i.shortfall_pct}%</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardShell>
  );
}

// -------------------------------------------------- Card C
function AgingStockCard({ data, threshold, onThresholdChange, loading, lastFetch, onRefresh, canEditSetting, onPersistThreshold }) {
  const [draft, setDraft] = useState(String(threshold ?? 30));
  useEffect(() => { setDraft(String(threshold ?? 30)); }, [threshold]);
  const isEmpty = !data || !data.items?.length;
  const actions = (
    <div className="flex items-center gap-1">
      <Settings2 className="h-3.5 w-3.5 text-neutral-400" />
      <Input value={draft} onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
             onBlur={() => { const v = Math.max(1, Math.min(365, Number(draft || 30))); onThresholdChange(v); setDraft(String(v)); }}
             onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
             className="h-8 w-14 text-xs" data-testid="card-aging-threshold" />
      <span className="text-xs text-neutral-500 mr-1">days</span>
      {canEditSetting && (
        <Button size="sm" variant="outline" onClick={() => onPersistThreshold(Number(draft))}
                className="h-8 text-xs px-2" data-testid="card-aging-save">Save default</Button>
      )}
    </div>
  );
  return (
    <CardShell
      testid="card-aging-stock" icon={Clock}
      title="Aging stock" subtitle={data ? `${data.items?.length || 0} row(s) beyond ${data.threshold_days}-day threshold` : ""}
      lastFetch={lastFetch} loading={loading} onRefresh={onRefresh}
      actions={actions}
      empty={isEmpty ? `No aging stock beyond ${threshold} days` : null}>
      {!isEmpty && (
        <div className="overflow-auto max-h-[240px] pr-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Distributor</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Qty KG</TableHead>
                <TableHead className="text-right">Days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((i, idx) => (
                <TableRow key={`${i.distributor_id}-${i.sku_id}`} data-testid={`row-aging-${idx}`}>
                  <TableCell className="text-sm">{i.distributor_code}<div className="text-xs text-neutral-500">{i.distributor_name}</div></TableCell>
                  <TableCell className="text-sm">{i.sku_code}<div className="text-xs text-neutral-500">{i.sku_name}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKg(i.current_qty_kg)}</TableCell>
                  <TableCell className="text-right"><Badge variant="secondary" className="text-[11px]">{i.days_since_last_movement ?? "—"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardShell>
  );
}

// -------------------------------------------------- Card D
function NegativeVarianceCard({ data, loading, lastFetch, onRefresh }) {
  const isEmpty = !data || !data.items?.length;
  return (
    <CardShell
      testid="card-negative-variance" icon={AlertTriangle}
      title="Negative variance alerts"
      subtitle={data ? `Last ${data.window_days}d · fulfilment < ordered by >${data.gap_pct_threshold}%` : ""}
      lastFetch={lastFetch} loading={loading} onRefresh={onRefresh}
      empty={isEmpty ? "No negative variance in the current window" : null}>
      {!isEmpty && (
        <div className="overflow-auto max-h-[260px] pr-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Ord.</TableHead>
                <TableHead className="text-right">Ful.</TableHead>
                <TableHead className="text-right">Gap%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((i, idx) => (
                <TableRow key={`${i.order_id}-${i.sku_code}-${idx}`} data-testid={`row-variance-${idx}`}>
                  <TableCell className="text-xs font-mono">{i.order_id.slice(0, 8)}<div className="text-[11px] text-neutral-500">{i.order_date}</div></TableCell>
                  <TableCell className="text-sm">{i.distributor_code}<div className="text-xs text-neutral-500">{i.distributor_name}</div></TableCell>
                  <TableCell className="text-sm">{i.sku_code}<div className="text-xs text-neutral-500">{i.sku_name}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKg(i.ordered_qty_kg)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKg(i.fulfilled_qty_kg)}</TableCell>
                  <TableCell className="text-right"><Badge variant="destructive" className="text-[11px]">{i.gap_pct}%</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardShell>
  );
}

// -------------------------------------------------- page
export default function InventoryDashboard() {
  const [stockData, setStockData] = useState(null);
  const [shortData, setShortData] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [varData, setVarData] = useState(null);

  const [stockAt, setStockAt] = useState(null);
  const [shortAt, setShortAt] = useState(null);
  const [agingAt, setAgingAt] = useState(null);
  const [varAt, setVarAt] = useState(null);

  const [stockBusy, setStockBusy] = useState(false);
  const [shortBusy, setShortBusy] = useState(false);
  const [agingBusy, setAgingBusy] = useState(false);
  const [varBusy, setVarBusy] = useState(false);

  const [threshold, setThreshold] = useState(30);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try { const u = await api.get("/auth/me"); setMe(u.data || u); } catch {}
      try {
        const s = await api.get("/dashboard/inventory/settings");
        setThreshold((s.data || s).aging_threshold_days);
      } catch {}
    })();
  }, []);

  const canEditSetting = useMemo(
    () => me && ["SUPER_ADMIN", "MANAGEMENT"].includes(me.role_code),
    [me]
  );

  const loadStock = useCallback(async () => {
    setStockBusy(true);
    try { const r = await api.get("/dashboard/inventory/stock-on-hand"); setStockData(r.data || r); setStockAt(Date.now()); }
    catch (e) { console.warn("stock", e); }
    finally { setStockBusy(false); }
  }, []);
  const loadShort = useCallback(async () => {
    setShortBusy(true);
    try { const r = await api.get("/dashboard/inventory/sku-shortfall"); setShortData(r.data || r); setShortAt(Date.now()); }
    catch (e) { console.warn("short", e); }
    finally { setShortBusy(false); }
  }, []);
  const loadAging = useCallback(async (t) => {
    setAgingBusy(true);
    try {
      const params = t ? { threshold_days: t } : undefined;
      const r = await api.get("/dashboard/inventory/aging-stock", { params });
      setAgingData(r.data || r); setAgingAt(Date.now());
    } catch (e) { console.warn("aging", e); }
    finally { setAgingBusy(false); }
  }, []);
  const loadVar = useCallback(async () => {
    setVarBusy(true);
    try { const r = await api.get("/dashboard/inventory/negative-variance"); setVarData(r.data || r); setVarAt(Date.now()); }
    catch (e) { console.warn("var", e); }
    finally { setVarBusy(false); }
  }, []);

  const loadAll = useCallback(() => {
    loadStock(); loadShort(); loadAging(threshold); loadVar();
  }, [loadStock, loadShort, loadAging, loadVar, threshold]);

  useEffect(() => { loadAll(); /* first load */ }, [loadAll]);

  // 30s polling with visibility API pause
  const pollRef = useRef(null);
  useEffect(() => {
    function start() {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        if (document.visibilityState === "hidden") return;
        loadAll();
      }, POLL_MS);
    }
    function stop() { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }
    start();
    document.addEventListener("visibilitychange", start);
    return () => { stop(); document.removeEventListener("visibilitychange", start); };
  }, [loadAll]);

  const persistThreshold = async (v) => {
    try {
      const r = await api.patch("/dashboard/inventory/settings", { aging_threshold_days: v });
      const val = (r.data || r).aging_threshold_days;
      setThreshold(val); loadAging(val);
      toast.success(`Default aging threshold set to ${val} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save threshold");
    }
  };
  const changeThreshold = (v) => { setThreshold(v); loadAging(v); };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="inventory-dashboard">
      <PageHeader
        title="Inventory Dashboard"
        subtitle="Real-time stock, shortfall, aging and variance — refreshes every 30 seconds"
        testid="inventory-dashboard-header"
        actions={
          <Button variant="default" onClick={loadAll} className="bg-[#E5197F] hover:bg-[#B31366]"
                  data-testid="dashboard-refresh-all">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh all
          </Button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <StockOnHandCard data={stockData} loading={stockBusy} lastFetch={stockAt} onRefresh={loadStock} />
        <SkuShortfallCard data={shortData} loading={shortBusy} lastFetch={shortAt} onRefresh={loadShort} />
        <AgingStockCard data={agingData} threshold={threshold} onThresholdChange={changeThreshold}
                        loading={agingBusy} lastFetch={agingAt} onRefresh={() => loadAging(threshold)}
                        canEditSetting={canEditSetting} onPersistThreshold={persistThreshold} />
        <NegativeVarianceCard data={varData} loading={varBusy} lastFetch={varAt} onRefresh={loadVar} />
      </div>
    </div>
  );
}
